import { useMemo } from "react";
import { ISeatPosition } from "types";

const pairWise = <T>(
  arr: Array<T>,
  callback: (current: T, next: T) => void,
) => {
  for (let i = 0; i < arr.length - 1; i += 1) {
    callback(arr[i], arr[i + 1]);
  }

  // close the loop.
  if (arr.length > 1) {
    callback(arr[arr.length - 1], arr[0]);
  }
};

const getOrthogonalVector = (
  a: ISeatPosition,
  b: ISeatPosition,
  origin: ISeatPosition,
) => ({ x: -(b.y - a.y) + origin.x, y: b.x - a.x + origin.y });

const findMidPoint = (a: ISeatPosition, b: ISeatPosition) => {
  const midX = (a.x - b.x) / 2 + b.x;
  const midY = (a.y - b.y) / 2 + b.y;

  return { x: midX, y: midY };
};

const getLength = (a: ISeatPosition, b: ISeatPosition) =>
  Math.sqrt((a.x - b.x) ** 2 + (a.y - b.y) ** 2);

const resize = (a: ISeatPosition, b: ISeatPosition, magnitude: number) => {
  const normalized = magnitude / getLength(a, b);

  const resized = {
    x: (a.x - b.x) * normalized + a.x,
    y: (a.y - b.y) * normalized + a.y,
  };

  return resized;
};

const subDividePosition = (
  a: ISeatPosition,
  b: ISeatPosition,
  angle: number,
): ISeatPosition => {
  const midpoint = findMidPoint(a, b);
  const ortho = getOrthogonalVector(a, b, midpoint);
  const midPointLength = getLength(midpoint, b);
  const length = midPointLength * Math.tan(angle);
  const normal = resize(midpoint, ortho, length);

  return { x: normal.x, y: normal.y };
};

const subdividePositions = (
  positions: ISeatPosition[],
  subdivisions: ISeatPosition[],
  angle: number,
  maxSubdivisions: number,
) => {
  if (subdivisions.length >= maxSubdivisions) {
    return;
  }

  const newPositions: ISeatPosition[] = [];

  pairWise(positions, (current, next) => {
    const newOverflowPosition = subDividePosition(current, next, angle);
    subdivisions.push(newOverflowPosition);
    newPositions.push(current, newOverflowPosition, next);
  });

  subdividePositions(newPositions, subdivisions, angle, maxSubdivisions);
};

const getCenter = (seats: ISeatPosition[]) => {
  const pointSum = seats.reduce((accumulatedPoints, currentSeat) => ({
    x: accumulatedPoints.x + currentSeat.x,
    y: accumulatedPoints.y + currentSeat.y,
  }));

  return { x: pointSum.x / seats.length, y: pointSum.y / seats.length };
};

const sortSeats = (seats: ISeatPosition[]) => {
  const center = getCenter(seats);
  return [...seats].sort((a, b) => {
    const angleA = Math.atan2(a.y - center.y, a.x - center.x);
    const angleB = Math.atan2(b.y - center.y, b.x - center.x);
    return angleA - angleB;
  });
};

export const useOverflowPositions = (
  positions: ISeatPosition[],
  maxOverflow: number,
) => {
  const sortedPositions = sortSeats(positions);
  const overflowPositions = useMemo(() => {
    const subdividedPositions: ISeatPosition[] = [];
    subdividePositions(
      sortedPositions,
      subdividedPositions,
      Math.PI / 4,
      maxOverflow,
    );

    return subdividedPositions;
  }, [sortedPositions, maxOverflow]);

  return { overflowPositions };
};
